import type {
  CalendarEventCategory,
  CodebooksCodebookToAdd,
  CodebooksCodebookToEdit,
  CodebooksOrderRequestCategoryToAdd,
  CodebooksOrderRequestCategoryToEdit,
  CodebooksServiceObjectRevisionCategoryToAdd,
  CodebooksServiceObjectRevisionCategoryToEdit,
  CodebooksWorkItemsWorkItemToAdd,
  CodebooksWorkItemsWorkItemToEdit,
  CustomCodebookItemToAdd,
  CustomCodebookItemToEdit,
  CustomCodebookToAdd,
  CustomCodebookToEdit,
  CustomerCategory,
  CustomerType,
  Group,
  GroupToAdd,
  GroupUsersToManage,
  InterventionCategory,
  InterventionCustomState,
  MaterialCategory,
  MaterialSupplier,
  OrderCategory,
  OrderCustomState,
  OrderRequestCategory,
  OrderRequestCustomState,
  PaymentType,
  ServiceObjectCustomState,
  ServiceObjectManufacturer,
  ServiceObjectManufacturerToAdd,
  ServiceObjectManufacturerToEdit,
  ServiceObjectRevisionCategory,
  Skill,
  SkillToAdd,
  SkillUsersToManage,
  SuperadminAllCodebooks,
  SuperadminCompanyCodebooksToSet,
  SuperadminCustomCodebook,
  SuperadminCustomCodebookItem,
  SuperadminServiceObjectCategory,
  SuperadminSuperAdminServiceObjectCategoryToAdd,
  SuperadminSuperAdminServiceObjectCategoryToEdit,
  VatOption,
  VatOptionToAdd,
  VatOptionToEdit,
  WorkItem
} from 'api/models/mainservice';
import { protectedApiClient } from 'api/support/baseApiClients';

export default {
  getCodebooks: async (companyId: string) => {
    const result = await protectedApiClient.get<SuperadminAllCodebooks>(
      `/superadmin/company/${companyId}/codebooks`
    );
    return result.data;
  },
  addCustomerCategory: async (params: { companyId: string; values: CodebooksCodebookToAdd }) => {
    const result = await protectedApiClient.post<CustomerCategory>(
      `/superadmin/company/${params.companyId}/customer/category`,
      params.values
    );
    return result.data;
  },
  editCustomerCategory: async (params: {
    companyId: string;
    categoryId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/customer/category/${params.categoryId}`,
      params.values
    );
    return result.data;
  },
  deleteCustomerCategory: async (params: { companyId: string; categoryId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/customer/category/${params.categoryId}`
    );
    return result.data;
  },
  addCustomerType: async (params: { companyId: string; values: CodebooksCodebookToAdd }) => {
    const result = await protectedApiClient.post<CustomerType>(
      `/superadmin/company/${params.companyId}/customer/type`,
      params.values
    );
    return result.data;
  },
  editCustomerType: async (params: {
    companyId: string;
    typeId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/customer/type/${params.typeId}`,
      params.values
    );
    return result.data;
  },
  deleteCustomerType: async (params: { companyId: string; typeId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/customer/type/${params.typeId}`
    );
    return result.data;
  },
  addMaterialCategory: async (params: { companyId: string; values: CodebooksCodebookToAdd }) => {
    const result = await protectedApiClient.post<MaterialCategory>(
      `/superadmin/company/${params.companyId}/material/category`,
      params.values
    );
    return result.data;
  },
  editMaterialCategory: async (params: {
    companyId: string;
    categoryId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/material/category/${params.categoryId}`,
      params.values
    );
    return result.data;
  },
  deleteMaterialCategory: async (params: { companyId: string; categoryId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/material/category/${params.categoryId}`
    );
    return result.data;
  },
  addOrderCategory: async (params: { companyId: string; values: CodebooksCodebookToAdd }) => {
    const result = await protectedApiClient.post<OrderCategory>(
      `/superadmin/company/${params.companyId}/order/category`,
      params.values
    );
    return result.data;
  },
  editOrderCategory: async (params: {
    companyId: string;
    categoryId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/order/category/${params.categoryId}`,
      params.values
    );
    return result.data;
  },
  deleteOrderCategory: async (params: { companyId: string; categoryId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/order/category/${params.categoryId}`
    );
    return result.data;
  },
  addServiceObjectCategory: async (params: {
    companyId: string;
    values: SuperadminSuperAdminServiceObjectCategoryToAdd;
  }) => {
    const result = await protectedApiClient.post<SuperadminServiceObjectCategory>(
      `/superadmin/company/${params.companyId}/serviceObject/category`,
      params.values
    );
    return result.data;
  },
  editServiceObjectCategory: async (params: {
    companyId: string;
    categoryId: string;
    values: SuperadminSuperAdminServiceObjectCategoryToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/serviceObject/category/${params.categoryId}`,
      params.values
    );
    return result.data;
  },
  deleteServiceObjectCategory: async (params: { companyId: string; categoryId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/serviceObject/category/${params.categoryId}`
    );
    return result.data;
  },
  addServiceObjectManufacturer: async (params: {
    companyId: string;
    values: ServiceObjectManufacturerToAdd;
  }) => {
    const result = await protectedApiClient.post<ServiceObjectManufacturer>(
      `/superadmin/company/${params.companyId}/serviceObject/manufacturer`,
      params.values
    );
    return result.data;
  },
  editServiceObjectManufacturer: async (params: {
    companyId: string;
    manufacturerId: string;
    values: ServiceObjectManufacturerToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/serviceObject/manufacturer/${params.manufacturerId}`,
      params.values
    );
    return result.data;
  },
  deleteServiceObjectManufacturer: async (params: {
    companyId: string;
    manufacturerId: string;
  }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/serviceObject/manufacturer/${params.manufacturerId}`
    );
    return result.data;
  },
  addServiceObjectRevisionCategory: async (params: {
    companyId: string;
    values: CodebooksServiceObjectRevisionCategoryToAdd;
  }) => {
    const result = await protectedApiClient.post<ServiceObjectRevisionCategory>(
      `/superadmin/company/${params.companyId}/serviceObject/revision`,
      params.values
    );
    return result.data;
  },
  editServiceObjectRevisionCategory: async (params: {
    companyId: string;
    id: string;
    values: CodebooksServiceObjectRevisionCategoryToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/serviceObject/revision/${params.id}`,
      params.values
    );
    return result.data;
  },
  deleteServiceObjectRevisionCategory: async (params: { companyId: string; id: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/serviceObject/revision/${params.id}`
    );
    return result.data;
  },
  addWorkItem: async (params: { companyId: string; values: CodebooksWorkItemsWorkItemToAdd }) => {
    const result = await protectedApiClient.post<WorkItem>(
      `/superadmin/company/${params.companyId}/workItem`,
      params.values
    );
    return result.data;
  },
  editWorkItem: async (params: {
    companyId: string;
    workItemId: string;
    values: CodebooksWorkItemsWorkItemToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/workItem/${params.workItemId}`,
      params.values
    );
    return result.data;
  },
  deleteWorkItem: async (params: { companyId: string; workItemId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/workItem/${params.workItemId}`
    );
    return result.data;
  },
  addMaterialSupplier: async (params: { companyId: string; values: CodebooksCodebookToAdd }) => {
    const result = await protectedApiClient.post<MaterialSupplier>(
      `/superadmin/company/${params.companyId}/material/supplier`,
      params.values
    );
    return result.data;
  },
  editMaterialSupplier: async (params: {
    companyId: string;
    supplierId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/material/supplier/${params.supplierId}`,
      params.values
    );
    return result.data;
  },
  deleteMaterialSupplier: async (params: { companyId: string; supplierId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/material/supplier/${params.supplierId}`
    );
    return result.data;
  },
  addVatOption: async (params: { companyId: string; values: VatOptionToAdd }) => {
    const result = await protectedApiClient.post<VatOption>(
      `/superadmin/company/${params.companyId}/vatOption`,
      params.values
    );
    return result.data;
  },
  editVatOption: async (params: {
    companyId: string;
    vatOptionId: string;
    values: VatOptionToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/vatOption/${params.vatOptionId}`,
      params.values
    );
    return result.data;
  },
  deleteVatOption: async (params: { companyId: string; vatOptionId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/vatOption/${params.vatOptionId}`
    );
    return result.data;
  },
  addCustomCodebook: async (params: { companyId: string; values: CustomCodebookToAdd }) => {
    const result = await protectedApiClient.post<SuperadminCustomCodebook>(
      `/superadmin/company/${params.companyId}/customCodebook`,
      params.values
    );
    return result.data;
  },
  editCustomCodebook: async (params: {
    companyId: string;
    customCodebookId: string;
    values: CustomCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/customCodebook/${params.customCodebookId}`,
      params.values
    );
    return result.data;
  },
  deleteCustomCodebook: async (params: { companyId: string; customCodebookId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/customCodebook/${params.customCodebookId}`
    );
    return result.data;
  },
  addCustomCodebookItem: async (params: {
    companyId: string;
    customCodebookId: string;
    values: CustomCodebookItemToAdd;
  }) => {
    const result = await protectedApiClient.post<SuperadminCustomCodebookItem>(
      `/superadmin/company/${params.companyId}/customCodebook/${params.customCodebookId}/item`,
      params.values
    );
    return result.data;
  },
  editCustomCodebookItem: async (params: {
    companyId: string;
    customCodebookId: string;
    customCodebookItemId: string;
    values: CustomCodebookItemToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/customCodebook/${params.customCodebookId}/item/${params.customCodebookItemId}`,
      params.values
    );
    return result.data;
  },
  deleteCustomCodebookItem: async (params: {
    companyId: string;
    customCodebookId: string;
    customCodebookItemId: string;
  }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/customCodebook/${params.customCodebookId}/item/${params.customCodebookItemId}`
    );
    return result.data;
  },
  addInterventionCategory: async (params: {
    companyId: string;
    values: CodebooksCodebookToAdd;
  }) => {
    const result = await protectedApiClient.post<InterventionCategory>(
      `/superadmin/company/${params.companyId}/order/intervention/category`,
      params.values
    );
    return result.data;
  },
  editInterventionCategory: async (params: {
    companyId: string;
    categoryId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/order/intervention/category/${params.categoryId}`,
      params.values
    );
    return result.data;
  },
  deleteInterventionCategory: async (params: { companyId: string; categoryId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/order/intervention/category/${params.categoryId}`
    );
    return result.data;
  },
  addOrderCustomState: async (params: { companyId: string; values: CodebooksCodebookToAdd }) => {
    const result = await protectedApiClient.post<OrderCustomState>(
      `/superadmin/company/${params.companyId}/order/customState`,
      params.values
    );
    return result.data;
  },
  editOrderCustomState: async (params: {
    companyId: string;
    orderCustomStateId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/order/customState/${params.orderCustomStateId}`,
      params.values
    );
    return result.data;
  },
  deleteOrderCustomState: async (params: { companyId: string; orderCustomStateId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/order/customState/${params.orderCustomStateId}`
    );
    return result.data;
  },
  addServiceObjectCustomState: async (params: {
    companyId: string;
    values: CodebooksCodebookToAdd;
  }) => {
    const result = await protectedApiClient.post<ServiceObjectCustomState>(
      `/superadmin/company/${params.companyId}/serviceObject/customState`,
      params.values
    );
    return result.data;
  },
  editServiceObjectCustomState: async (params: {
    companyId: string;
    serviceObjectCustomStateId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/serviceObject/customState/${params.serviceObjectCustomStateId}`,
      params.values
    );
    return result.data;
  },
  deleteServiceObjectCustomState: async (params: {
    companyId: string;
    serviceObjectCustomStateId: string;
  }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/serviceObject/customState/${params.serviceObjectCustomStateId}`
    );
    return result.data;
  },
  addOrderRequestCustomState: async (params: {
    companyId: string;
    values: CodebooksCodebookToAdd;
  }) => {
    const result = await protectedApiClient.post<OrderRequestCustomState>(
      `/superadmin/company/${params.companyId}/orderRequest/customState`,
      params.values
    );
    return result.data;
  },
  editOrderRequestCustomState: async (params: {
    companyId: string;
    orderRequestCustomStateId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/orderRequest/customState/${params.orderRequestCustomStateId}`,
      params.values
    );
    return result.data;
  },
  deleteOrderRequestCustomState: async (params: {
    companyId: string;
    orderRequestCustomStateId: string;
  }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/orderRequest/customState/${params.orderRequestCustomStateId}`
    );
    return result.data;
  },
  addGroup: async (params: { companyId: string; values: GroupToAdd }) => {
    const result = await protectedApiClient.post<Group>(
      `/superadmin/company/${params.companyId}/group`,
      params.values
    );

    return result.data;
  },
  editGroup: async (params: {
    companyId: string;
    groupId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/group/${params.groupId}`,
      params.values
    );

    return result.data;
  },
  deleteGroup: async (params: { companyId: string; groupId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/group/${params.groupId}`
    );

    return result.data;
  },
  manageGroupUsers: async (params: {
    companyId: string;
    groupId: string;
    values: GroupUsersToManage;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/group/${params.groupId}/manageUsers`,
      params.values
    );

    return result.data;
  },
  addSkill: async (params: { companyId: string; values: SkillToAdd }) => {
    const result = await protectedApiClient.post<Skill>(
      `/superadmin/company/${params.companyId}/skill`,
      params.values
    );

    return result.data;
  },
  editSkill: async (params: {
    companyId: string;
    skillId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/skill/${params.skillId}`,
      params.values
    );

    return result.data;
  },
  deleteSkill: async (params: { companyId: string; skillId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/skill/${params.skillId}`
    );

    return result.data;
  },
  manageSkillUsers: async (params: {
    companyId: string;
    skillId: string;
    values: SkillUsersToManage;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/skill/${params.skillId}/manageUsers`,
      params.values
    );

    return result.data;
  },
  addCodebooksBatch: async (params: {
    companyId: string;
    values: SuperadminCompanyCodebooksToSet;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/codebooks/set`,
      params.values
    );

    return result.data;
  },
  addInterventionCustomState: async (params: {
    companyId: string;
    values: CodebooksCodebookToAdd;
  }) => {
    const result = await protectedApiClient.post<InterventionCustomState>(
      `/superadmin/company/${params.companyId}/intervention/customState`,
      params.values
    );
    return result.data;
  },
  editInterventionCustomState: async (params: {
    companyId: string;
    interventionCustomStateId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/intervention/customState/${params.interventionCustomStateId}`,
      params.values
    );
    return result.data;
  },
  deleteInterventionCustomState: async (params: {
    companyId: string;
    interventionCustomStateId: string;
  }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/intervention/customState/${params.interventionCustomStateId}`
    );
    return result.data;
  },
  addPaymentType: async (params: { companyId: string; values: CodebooksCodebookToAdd }) => {
    const result = await protectedApiClient.post<PaymentType>(
      `/superadmin/company/${params.companyId}/paymentType`,
      params.values
    );
    return result.data;
  },
  editPaymentType: async (params: {
    companyId: string;
    paymentTypeId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/paymentType/${params.paymentTypeId}`,
      params.values
    );
    return result.data;
  },
  deletePaymentType: async (params: { companyId: string; paymentTypeId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/paymentType/${params.paymentTypeId}`
    );
    return result.data;
  },
  addCalendarEventCategory: async (params: {
    companyId: string;
    values: CodebooksCodebookToAdd;
  }) => {
    const result = await protectedApiClient.post<CalendarEventCategory>(
      `/superadmin/company/${params.companyId}/calendarEvent/category`,
      params.values
    );
    return result.data;
  },
  editCalendarEventCategory: async (params: {
    companyId: string;
    calendarEventCategoryId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/calendarEvent/category/${params.calendarEventCategoryId}`,
      params.values
    );
    return result.data;
  },
  deleteCalendarEventCategory: async (params: {
    companyId: string;
    calendarEventCategoryId: string;
  }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/calendarEvent/category/${params.calendarEventCategoryId}`
    );
    return result.data;
  },
  addOrderRequestCategory: async (params: {
    companyId: string;
    values: CodebooksOrderRequestCategoryToAdd;
  }) => {
    const result = await protectedApiClient.post<OrderRequestCategory>(
      `/superadmin/company/${params.companyId}/orderRequest/category`,
      params.values
    );
    return result.data;
  },
  editOrderRequestCategory: async (params: {
    companyId: string;
    orderRequestCategoryId: string;
    values: CodebooksOrderRequestCategoryToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/orderRequest/category/${params.orderRequestCategoryId}`,
      params.values
    );
    return result.data;
  },
  deleteOrderRequestCategory: async (params: {
    companyId: string;
    orderRequestCategoryId: string;
  }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/orderRequest/category/${params.orderRequestCategoryId}`
    );
    return result.data;
  }
};
